import {
  getCampaignOption,
  getEntityOption,
  getOrganizationOption
} from '../helpers/entityOptionHelper';
import { countries } from './countries';

export type baseOptionsType = {
  name: any;
  organization: { urlPath: any };
  urlPath: any;
  url: any;
  id: any;
};

export const lookupEntities = {
  CampaignLookup: 'page/campaign',
  OrganizationLookup: 'organization',
  FundraiserLookup: 'page/fundraiser',
  TeamLookup: 'page/team',
  EventLookup: 'page/event',
  TicketLookup: 'ticket',
  MerchandiseLookup: 'merchandise',
  CampaignOrganizerLookup: 'CampaignOrganizer',
  FranchiseLookup: 'Franchise',
  EventOrganizerLookup: 'EventOrganizer'
};

export const fitnessDateRuleOptions = [
  {
    value: null,
    text: '--empty--'
  },
  {
    value: 'applyOnAll',
    text: 'Apply on all'
  },
  {
    value: 'applyOnlyOnCampaignAndHigher',
    text: 'Apply only on campaign and higher'
  },
  {
    value: 'applyOnlyOnChildPages',
    text: 'Apply only on child pages'
  }
];

export const treeLookupEntities = {
  CollectionTreeLookup: 'page/collection'
};

export const applyBaseFilter = {
  [lookupEntities.CampaignLookup]: true,
  [lookupEntities.OrganizationLookup]: false,
  [lookupEntities.FundraiserLookup]: true,
  [lookupEntities.TeamLookup]: true,
  [lookupEntities.EventLookup]: false,
  [lookupEntities.TicketLookup]: false,
  [lookupEntities.MerchandiseLookup]: false,
  [lookupEntities.FranchiseLookup]: false
};

export const applyAdditionalFilter = {
  [lookupEntities.CampaignLookup]: false,
  [lookupEntities.OrganizationLookup]: false,
  [lookupEntities.FundraiserLookup]: false,
  [lookupEntities.TeamLookup]: false,
  [lookupEntities.EventLookup]: false,
  [lookupEntities.TicketLookup]: true,
  [lookupEntities.MerchandiseLookup]: true,
  [lookupEntities.FranchiseLookup]: false
};

export const baseOptions = {
  [lookupEntities.CampaignLookup]: {
    getItem(item: baseOptionsType) {
      return getCampaignOption(item);
    }
  },
  [lookupEntities.OrganizationLookup]: {
    getItem(item: baseOptionsType) {
      return getOrganizationOption(item);
    }
  },
  [lookupEntities.FundraiserLookup]: {
    getItem(item: baseOptionsType) {
      return getEntityOption(item);
    }
  },
  [lookupEntities.TeamLookup]: {
    getItem(item: baseOptionsType) {
      return getEntityOption(item);
    }
  },
  [lookupEntities.EventLookup]: {},
  [lookupEntities.TicketLookup]: {},
  [lookupEntities.MerchandiseLookup]: {},
  [lookupEntities.CampaignOrganizerLookup]: {
    getItem(item: baseOptionsType) {
      return getEntityOption(item);
    }
  },
  [lookupEntities.FranchiseLookup]: {
    getItem(item: baseOptionsType) {
      return getEntityOption(item);
    }
  },
  [lookupEntities.EventOrganizerLookup]: {
    getItem(item: baseOptionsType) {
      return getEntityOption(item);
    }
  }
};

export const filterSelectBaseOptions = {
  PageType: [
    {
      value: 'fundraiser',
      text: 'Fundraiser'
    },
    {
      value: 'team',
      text: 'Team'
    },
    {
      value: 'collection',
      text: 'Superteam'
    }
  ],
  AdminContextType: [
    {
      value: 'system',
      text: 'System'
    },
    {
      value: 'organization',
      text: 'Organisation'
    },
    {
      value: 'campaign',
      text: 'Campaign'
    },
    {
      value: 'event',
      text: 'Event'
    },
    {
      value: 'campaignOrganizer',
      text: 'Campaign Organiser'
    },
    {
      value: 'eventOrganizer',
      text: 'Event Organizer'
    }
  ],
  AdminRole: [
    {
      value: 'owner',
      text: 'Owner'
    },
    {
      value: 'admin',
      text: 'Admin'
    },
    {
      value: 'viewer',
      text: 'Viewer'
    }
  ],
  Country: Object.entries(countries).map(([code, country]) => ({
    value: code,
    text: country.label
  }))
};

export const NUMBER_TYPES = ['Decimal', 'Int32', 'Int64'];
export const SELECT_TYPES = [
  'DonationType',
  'CampaignType',
  'PageType',
  'AccountStatus',
  'Role',
  'SaleType',
  'DonationSubscriptionStatus',
  'EventAmbassadorStatus',
  'TierType',
  'EventTier',
  'AdminContextType',
  'AdminRole',
  'DataRetentionActivityType',
  'DataRetentionFlagStatus',
  'Country',
  'GiftAidClaimStatus'
];

// Enum filters whose options are labelled from i18n rather than shown as the
// raw enum name. The API sends each option as the PascalCase enum name; the
// label is read from `${prefix}.${name with its first letter lower-cased}`, so
// it can share the camelCase keys the detail views already use. The option
// value is still sent back exactly as received.
export const translatedSelectOptions: { [dataType: string]: string } = {
  GiftAidClaimStatus: 'donation.donation-view.gift-aid.status'
};
export const LOOKUP_TYPES = [
  'TeamLookup',
  'FundraiserLookup',
  'OrganizationLookup',
  'CampaignLookup',
  'EventLookup',
  'TicketLookup',
  'MerchandiseLookup',
  'CampaignOrganizerLookup',
  'FranchiseLookup',
  'EventOrganizerLookup'
];
export const TREE_LOOKUP_TYPES = ['CollectionTreeLookup'];
export const ORGANIZATION_ID_FILTER = 'organizationId';
export const CAMPAIGN_ORGANIZER_ID_FILTER = 'campaignOrganizerId';
export const EVENT_ID_FILTER = 'eventId';
export const OBJECT_ORGANIZATION_ID_FILTER = 'organization.id';
export const FRANCHISE_ID_FILTER = 'franchiseId';
export const IS_EVENT_ADMIN = 'isEventAdmin';
export const IS_EVENT_ORGANIZER_ADMIN = 'isEventOrganizerAdmin';
export const IS_CAMPAIGN_ORGANIZER_ADMIN = 'isCampaignOrganizerAdmin';
export const IS_SYSTEM_ADMIN_FILTER = 'isSysAdmin';
